import React from 'react';
import './index.scss';
import _ from 'lodash';
import {
  Button, Spinner, Card, Form, ListGroup,
} from 'react-bootstrap';
import { useQueryClient } from 'react-query';
import * as Sentry from '@sentry/browser';
import { Link, useHistory } from 'react-router-dom';
import { BsFolderCheck as EasyImportIcon, BsCardChecklist as CustomImportIcon } from 'react-icons/bs';
import { useQueryPostGCPProjects, useQueryGetUnmanagedProjects } from 'hooks/queries';
import { useNotificationToaster } from 'notifications/api';
import VError from 'verror';
import * as context from './context';
import { TreeNode } from './tree-node';
import { Filter } from './filter';
import { useSelectedProjects, useParsedQueryString, useUpdatedTo } from './hooks';
import { routeParamsToCloseImportProjectsDrawer, routeParamsToOpenImportProjectsDrawer } from './project-importer-drawer';

const FIFTEEN_SECONDS = 15000;

const Tree = () => {
  const view = useParsedQueryString()?.view;
  return (
    <section className="container py-4 h-100">

      {(view === 'custom') ? (<CustomView />) : (<EasyView />)}

    </section>
  );
};

const ProjectsSelected = ({ prefix, as, ...props }) => {
  const { numberOfProjectsInTotal, projectsSelectedToBeImported, selectedAll } = useSelectedProjects();
  const selected = (<strong>{projectsSelectedToBeImported?.length}</strong>);
  const total = (<strong>{numberOfProjectsInTotal}</strong>);
  const some = (
    <div>
      {prefix || 'Importing'}
      {' '}
      {selected}
      {' '}
      of
      {' '}
      {total}
      {' '}
      projects.
    </div>
  );
  const all = (
    <div>
      {prefix || 'Importing'}
      {' '}
      all
      {' '}
      {selected}
      {' '}
      projects.
    </div>
  );
  const Component = as || 'span';

  return (<Component {...props}>{selectedAll ? all : some}</Component>);
};

const EasyImportCard = () => {
  const { projectsSelectedToBeImported } = useSelectedProjects();
  const numberOfProjectsSelectedToBeImported = projectsSelectedToBeImported?.length;
  const disabled = (numberOfProjectsSelectedToBeImported <= 0);
  return (
    <Card className="flex-fill mb-8">
      <Card.Body className="p-8">
        <div className="d-flex gap-6 align-items-center mb-6">
          <EasyImportIcon className="import-icon" />
          <h2 className="h5 mb-0">Easy Import</h2>
        </div>
        <p className="mb-0 lh-sm">
          Use this option to
          {' '}
          <strong>
            import
            {' '}
            {numberOfProjectsSelectedToBeImported}
            {' '}
            project
            {(numberOfProjectsSelectedToBeImported === 1) ? '' : 's'}
          </strong>
          {' '}
          from your Google organization. This is the
          {' '}
          <strong>recommended option</strong>
          {' '}
          as it provides easy access to any project via the Rackspace management portal.
        </p>
      </Card.Body>
      <Card.Footer className="border-top-0 px-8 pb-8">
        <ProjectsLinkButtonsBar disabled={disabled} projectsSelectedToBeImported={projectsSelectedToBeImported} isCustomView={false} />
      </Card.Footer>
    </Card>
  );
};
const CustomImportCard = () => {
  const toCustomView = useUpdatedTo({ view: 'custom' });
  return (
    <Card className="flex-fill">
      <Card.Body className="p-8">
        <div className="d-flex gap-6 align-items-center mb-6">
          <CustomImportIcon className="import-icon" />
          <h2 className="h5 mb-0">Custom Import</h2>
        </div>
        <p className="mb-0 lh-sm">Use this option to only import specific projects from your Google organization. Projects that are not imported will not be accessible from your Rackspace management portal.</p>
      </Card.Body>
      <Card.Footer className="border-top-0 px-8 pb-8">
        <Button as={Link} className="w-100" variant="outline-primary" to={toCustomView}>Custom Import</Button>
      </Card.Footer>
    </Card>
  );
};
const EasyView = () => (
  <div>
    <p>Import your GCP projects into Rackspace Management Portal. Select one of the options below:</p>
    <div className="rax-easy-view-cards">
      <EasyImportCard />
      <CustomImportCard />
    </div>
  </div>
);

const CustomView = () => {
  const checkRef = React.useRef(null);
  const { selectAll, setSelectedItems } = React.useContext(context.selectedItems) || {};
  const { projectsSelectedToBeImported, selectedAll, selectedSome } = useSelectedProjects();
  const disabled = projectsSelectedToBeImported?.length <= 0;
  const toggleSelectAll = React.useCallback(({ target: { checked } }) => {
    if (checked) {
      selectAll();
    } else {
      setSelectedItems({});
    }
  }, [selectAll, setSelectedItems]);
  React.useEffect(() => {
    _.set(checkRef, 'current.indeterminate', !!selectedSome);
  }, [selectedSome, checkRef]);
  return (
    <div className="rax-mgcp-project-import-grid h-100">
      <Filter />

      <ListGroup>
        <ListGroup.Item className="border-bottom-0">
          <div className="rax-mgcp-select-all-toolbar">
            <Form.Check onChange={toggleSelectAll} ref={checkRef} checked={selectedAll} id="select-all-projects-check" className="toggle-all cursor-pointer" />
            <ProjectsSelected prefix="Selected" as="label" htmlFor="select-all-projects-check" className="cursor-pointer" />
          </div>
        </ListGroup.Item>
        <ul className="rax-tree-node m-0 p-0" aria-label="List of GCP Projects Available to Import">
          <TreeNode targetFolder="/" />
        </ul>
      </ListGroup>

      <ProjectsLinkButtonsBar disabled={disabled} projectsSelectedToBeImported={projectsSelectedToBeImported} isCustomView />
    </div>
  );
};

const getTextOnImportButton = (isCustomView, numberOfProjectsSelectedToBeImported) => (isCustomView
  ? `Import ${numberOfProjectsSelectedToBeImported} Project${(numberOfProjectsSelectedToBeImported === 1) ? '' : 's'}`
  : 'Easy Import');

const ProjectsLinkButtonsBar = ({ disabled, projectsSelectedToBeImported, isCustomView }) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const toSimpleView = useUpdatedTo(routeParamsToOpenImportProjectsDrawer());
  const toClosed = useUpdatedTo(routeParamsToCloseImportProjectsDrawer());
  const numberOfProjectsSelectedToBeImported = projectsSelectedToBeImported?.length;
  const projectsPayload = _.map(projectsSelectedToBeImported, (project) => {
    project.source = 'assumption';
    return _.pick(project, ['projectId', 'projectNumber', 'name', 'source']);
  });
  const { notifyMultiple } = useNotificationToaster();
  const { mutate: mutateJanusProjects, isLoading } = useQueryPostGCPProjects();
  const onImport = React.useCallback(() => {
    mutateJanusProjects(projectsPayload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries('janus-projects');
        const acceptedProjects = _.get(response, 'accepted', []);
        const rejectedProjects = _.get(response, 'rejected', []);
        const detailsMessage = (
          <p>
            Rackspace was unable to import
            {' '}
            {rejectedProjects?.length === 1 ? rejectedProjects[0].projectId : 'some of your projects'}
            .
            {rejectedProjects?.length === 1 ? 'It' : 'These'}
            {' '}
            will remain available in the import list if you wish to retry. If the issue persists, please contact your CSM for assistance
          </p>
        );
        const acceptedMessage = _.isEmpty(acceptedProjects) ? false : {
          autohide: true,
          delay: FIFTEEN_SECONDS,
          title: `Projects Imported (${acceptedProjects.length})`,
          details: 'Rackspace is provisioning project defaults according to your account configuration.',
          category: 'success',
        };
        const rejectedMessage = _.isEmpty(rejectedProjects) ? false : {
          autohide: false,
          title: `Projects Failed (${rejectedProjects.length})`,
          details: <div>{ detailsMessage }</div>,
          category: 'error',
        };

        notifyMultiple(_.compact([acceptedMessage, rejectedMessage]));
      },
      onError: (errorResponse) => {
        const detailsMessage = _.get(VError.info(errorResponse), ['body', 'error', 'message'], 'Could not complete your request. If the issue persists, please contact your CSM for assistance.');
        Sentry.captureException(errorResponse);
        notifyMultiple([{
          autohide: false,
          title: 'Failed to Import projects',
          details: detailsMessage,
          category: 'error',
        }]);
      },
      onSettled: () => {
        history.replace(toClosed);
      },
    });
  }, [toClosed, queryClient, notifyMultiple, history, projectsPayload, mutateJanusProjects]);
  return (
    <div className="d-flex gap-4">
      <Button className="w-100" type="button" disabled={disabled || isLoading} onClick={onImport}>
        { isLoading
          ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          )
          : getTextOnImportButton(isCustomView, numberOfProjectsSelectedToBeImported)}
      </Button>
      { isCustomView && <Button className="w-100" as={Link} variant="outline-primary" to={toSimpleView}>Cancel</Button> }
    </div>
  );
};

const allProjectSelection = (allProjects) => _.reduce(allProjects, (result, { id }) => ({ ...result, [id]: true }), {});

const ProjectSelector = ({ allProjects }) => {
  const [selectedItems, setSelectedItems] = React.useState(allProjectSelection(allProjects));
  const [filter, setFilter] = React.useState('');
  const selectAll = React.useCallback(() => {
    setSelectedItems(allProjectSelection(allProjects));
  }, [allProjects, setSelectedItems]);
  React.useEffect(() => {
    selectAll();
  }, [selectAll]);

  return (
    <context.selectedItems.Provider value={{ selectedItems, setSelectedItems, selectAll }}>
      <context.filter.Provider value={{ filter, setFilter }}>
        <div className="rax-mgcp-project-selector h-100">
          <Tree />
        </div>
      </context.filter.Provider>
    </context.selectedItems.Provider>
  );
};

export const TreeView = () => {
  const query = useQueryGetUnmanagedProjects();
  return (<ProjectSelector allProjects={query?.data?.allProjects} />);
};

export default TreeView;
